import {L10nTranslationService} from 'angular-l10n';
import {isSmartSecurityKey} from '../components/smart-security';
import {ACCOUNT_TYPE_LABEL_MAP, AccountType} from '../enums/account-type';
import {
    Exchange,
    EXCHANGE_NAMING,
    isExchangeWithSplitAccount,
    SMART_ACCOUNT_EXCHANGE_API_VALUE
} from '../enums/exchange';
import {ExchangeAccountStatus} from '../enums/exchange-account-status';
import {ExchangeAccount} from '../models/exchange-account';
import {SecurityKey} from '../models/security-key';

export interface DisplayExchangeAccount {
    account: ExchangeAccount;
    name: string;
    exchange?: string; // Need only for Backoffice
    status?: string; // Need only for Backoffice
    isDisabled: boolean;
}

const EXCHANGE_ACCOUNT_STATUS_DICT: {[exchangeAccountStatus in ExchangeAccountStatus]: string} = {
    [ExchangeAccountStatus.ACTIVE]: 'enums.account-status.active',
    [ExchangeAccountStatus.DELETED_FROM_EXCHANGE]: 'enums.account-status.deleted_from_exchange',
    [ExchangeAccountStatus.DELETED_FROM_SYSTEM]: 'enums.account-status.deleted_from_system'
};

export class DisplayExchangeAccountFactory {

    public static createDisplayAccount(account: ExchangeAccount): DisplayExchangeAccount {
        const {status, exchange} = account || {} as ExchangeAccount;

        const correctExchange = DisplayExchangeAccountFactory.getAccountCorrectExchangeForDisplay(exchange);
        const correctDisplayName = DisplayExchangeAccountFactory.getDisplayAccountName(account, false);

        return {
            account,
            name:       correctDisplayName,
            exchange:   EXCHANGE_NAMING[correctExchange] || correctExchange,
            status:     EXCHANGE_ACCOUNT_STATUS_DICT[status] || status,
            isDisabled: status !== ExchangeAccountStatus.ACTIVE || false
        };
    }

    public static createDisplayAccountOrSmart(
        account: ExchangeAccount,
        securityKey: SecurityKey
    ): DisplayExchangeAccount {
        return securityKey && isSmartSecurityKey(securityKey)
            ? {
                account:    null,
                name:       EXCHANGE_NAMING[Exchange.SMART],
                isDisabled: false
            }
            : DisplayExchangeAccountFactory.createDisplayAccount(account);
    }

    public static createDisplayAccounts(accounts: ExchangeAccount[]): DisplayExchangeAccount[] {
        return accounts.map(DisplayExchangeAccountFactory.createDisplayAccount);
    }

    public static getDisplayAccountName(
        account: ExchangeAccount
    ): string;

    public static getDisplayAccountName(
        account: ExchangeAccount,
        supportSplitAccount: false
    ): string;

    public static getDisplayAccountName(
        account: ExchangeAccount,
        supportSplitAccount: false,
        accountType?: AccountType,
        translationService?: L10nTranslationService
    ): string;

    public static getDisplayAccountName(
        account: ExchangeAccount,
        supportSplitAccount: true,
        accountType: AccountType,
        translationService: L10nTranslationService
    ): string;

    public static getDisplayAccountName(
        account: ExchangeAccount,
        supportSplitAccount: boolean = false,
        accountType?: AccountType,
        translationService?: L10nTranslationService
    ): string {
        let resultDisplayName: string = '';

        if (account) {
            const correctExchange = DisplayExchangeAccountFactory.getAccountCorrectExchangeForDisplay(account.exchange);
            const correctSimpleDisplayName = account.displayName || (
                correctExchange === Exchange.SMART
                    ? EXCHANGE_NAMING[Exchange.SMART]
                    : ''
            );
            resultDisplayName = correctSimpleDisplayName;

            if (
                supportSplitAccount
                && account
                && isExchangeWithSplitAccount(account.exchange)
            ) {
                const knownDisplayAccountType = ACCOUNT_TYPE_LABEL_MAP[accountType || account.type];

                if (knownDisplayAccountType) {
                    resultDisplayName = [
                        correctSimpleDisplayName,
                        translationService.translate(knownDisplayAccountType)
                    ].join(' ');
                }
            }
        }

        return resultDisplayName;
    }

    public static getAccountCorrectExchangeForDisplay(
        origExchange: Exchange | typeof SMART_ACCOUNT_EXCHANGE_API_VALUE
    ): Exchange {
        return origExchange === SMART_ACCOUNT_EXCHANGE_API_VALUE
            ? Exchange.SMART
            : origExchange;
    }

}
